import LoadFailed from '../LoadFailed';
import { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, Text, Spinner, Button } from 'frosted-ui';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { format, eachDayOfInterval, startOfDay } from 'date-fns';
import { api } from '../../services/api';
import { formatRpv, variantLabel } from '../../utils/variants';
import { useIsMobile } from '../../hooks/useIsMobile';
import type { VariantTimeSeries, VariantTimeSeriesResponse } from '../../types';

// Theme tokens, one per variant name so a price keeps its colour whatever
// is shown. Blue/orange first: the Control/Challenger pair stays apart
// under colour-vision deficiency.
const VARIANT_COLORS: Record<string, string> = {
  A: 'var(--blue-9)',
  B: 'var(--orange-9)',
  C: 'var(--green-9)',
};
const FALLBACK_COLOR = 'var(--violet-9)';

type TimeRange = '7d' | '14d' | '30d' | 'all';
export type TrendMetric = 'rpv' | 'bought';

interface PerformanceTrendProps {
  experimentId: string;
  companyId: string;
  since: string; // when the test went live (or was created); running totals start here
  until?: string; // when it ended, for an ended test
  currency: string;
  boughtLabel: string; // the cards' label: "Bought", "Started (trial or paid)", "Unlocked"
  // False: revenue can't judge this test, so only Bought is charted.
  revenueComparable?: boolean;
}

export interface TrendPoint {
  date: string; // display label
  [variantName: string]: string | number | null;
}

/**
 * Running revenue per visitor (or share who bought) per variant, one point
 * per day from `start` to `end`. Each variant's running totals add the
 * people first seen that day, so the last point matches the cards, whether or
 * not that day had a visit. Before a variant's first visitor there's nothing
 * to divide by, so its line starts there.
 */
export function buildTrend(
  variants: VariantTimeSeries[],
  start: Date,
  end: Date,
  metric: TrendMetric
): TrendPoint[] {
  const days = eachDayOfInterval({ start: startOfDay(start), end: startOfDay(end) });
  const byVariant = variants.map((v) => ({
    name: v.variantName,
    points: new Map(v.data.map((p) => [p.date, p])),
    visitors: 0,
    starters: 0,
    net: 0,
  }));
  return days.map((day) => {
    const key = format(day, 'yyyy-MM-dd');
    const point: TrendPoint = { date: format(day, 'MMM d') };
    for (const v of byVariant) {
      const p = v.points.get(key);
      if (p) {
        v.visitors += p.newVisitors;
        v.starters += p.newStarters;
        v.net += p.netRevenue;
      }
      point[v.name] =
        v.visitors === 0
          ? null
          : metric === 'rpv'
            ? Math.round((v.net / v.visitors) * 100) / 100
            : Math.min(100, Math.round((v.starters / v.visitors) * 1000) / 10);
    }
    return point;
  });
}

const RANGE_DAYS: Record<TimeRange, number | null> = { '7d': 7, '14d': 14, '30d': 30, all: null };

export default function PerformanceTrend({
  experimentId,
  companyId,
  since,
  until,
  currency,
  boughtLabel,
  revenueComparable = true,
}: PerformanceTrendProps) {
  const isMobile = useIsMobile();
  const [timeRange, setTimeRange] = useState<TimeRange>('all');
  const [metric, setMetric] = useState<TrendMetric>(revenueComparable ? 'rpv' : 'bought');
  const [data, setData] = useState<VariantTimeSeriesResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  // Always fetch the whole test: running totals need every day since it began.
  const start = useMemo(() => startOfDay(new Date(since)), [since]);
  const end = useMemo(() => (until ? new Date(until) : new Date()), [until]);

  const loadData = useCallback(async () => {
    setLoading(true);
    setFailed(false);
    try {
      setData(await api.getVariantTimeSeriesData(experimentId, companyId, start, end, 'day'));
    } catch (err) {
      console.error('Failed to load trend data:', err);
      setFailed(true);
    } finally {
      setLoading(false);
    }
  }, [experimentId, companyId, start, end]);

  useEffect(() => {
    if (experimentId && companyId) {
      loadData();
    } else {
      setLoading(false);
    }
  }, [experimentId, companyId, loadData]);

  const variants = data?.variants ?? [];
  const chartData = useMemo(() => {
    const all = buildTrend(variants, start, end, metric);
    const days = RANGE_DAYS[timeRange];
    return days === null ? all : all.slice(-(days + 1));
  }, [variants, start, end, metric, timeRange]);

  if (loading) {
    return (
      <Card size="2">
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
          <Spinner size="2" />
          <Text size="2" color="gray">
            Loading performance data...
          </Text>
        </div>
      </Card>
    );
  }

  if (failed) return <LoadFailed what="the chart" onRetry={loadData} />;

  if (variants.length === 0) {
    return (
      <Card size="2">
        <Text size="2" weight="medium" style={{ display: 'block', marginBottom: 'var(--space-3)' }}>
          Over time
        </Text>
        <Text size="2" color="gray">
          Nothing to chart yet. Trends appear once your test gets visitors.
        </Text>
      </Card>
    );
  }

  // Nothing but zeros: one $0 line, not an axis running up to money nobody has.
  const allZero = chartData.every((row) => variants.every((v) => !row[v.variantName]));
  const formatValue = (value: number) =>
    metric === 'rpv' ? formatRpv(value, currency) : `${value.toFixed(1)}%`;
  const toggle = (pressed: boolean) => ({
    size: (isMobile ? '2' : '1') as '1' | '2',
    variant: (pressed ? 'solid' : 'soft') as 'solid' | 'soft',
    color: pressed ? undefined : ('gray' as const),
    'aria-pressed': pressed,
    style: isMobile ? { minWidth: 44 } : undefined,
  });

  return (
    <Card size="2">
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 'var(--space-2)',
          marginBottom: 'var(--space-3)',
        }}
      >
        <Text size="2" weight="medium">
          Over time
        </Text>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-2)' }}>
          {revenueComparable && (
            <div role="group" aria-label="Metric" style={{ display: 'flex', gap: 'var(--space-1)' }}>
              <Button {...toggle(metric === 'rpv')} onClick={() => setMetric('rpv')}>
                Revenue per visitor
              </Button>
              <Button {...toggle(metric === 'bought')} onClick={() => setMetric('bought')}>
                {boughtLabel}
              </Button>
            </div>
          )}
          <div
            role="group"
            aria-label="Time range"
            style={{ display: 'flex', gap: 'var(--space-1)' }}
          >
            {(Object.keys(RANGE_DAYS) as TimeRange[]).map((range) => (
              <Button
                key={range}
                {...toggle(timeRange === range)}
                onClick={() => setTimeRange(range)}
              >
                {range === 'all' ? 'All' : range}
              </Button>
            ))}
          </div>
        </div>
      </div>

      <Text size="1" color="gray" style={{ display: 'block', marginBottom: 'var(--space-3)' }}>
        {metric === 'rpv'
          ? 'Revenue per visitor since the test began, as of each day'
          : `${boughtLabel}, share of visitors since the test began, as of each day`}
        .
      </Text>

      <div style={{ width: '100%', height: 220 }}>
        <ResponsiveContainer>
          <LineChart data={chartData} margin={{ top: 5, right: 16, left: 0, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--gray-a5)" vertical={false} />
            <XAxis
              dataKey="date"
              minTickGap={12}
              tick={{ fontSize: 11, fill: 'var(--gray-11)' }}
              tickLine={{ stroke: 'var(--gray-a6)' }}
              axisLine={{ stroke: 'var(--gray-a6)' }}
            />
            <YAxis
              {...(allZero ? { domain: [0, 1], ticks: [0] } : {})}
              width={56}
              tick={{ fontSize: 11, fill: 'var(--gray-11)' }}
              tickLine={false}
              axisLine={false}
              tickFormatter={(value: number) => formatValue(value)}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: 'var(--color-panel-solid, var(--gray-1))',
                border: '1px solid var(--gray-a6)',
                borderRadius: 'var(--radius-2)',
                color: 'var(--gray-12)',
                fontSize: 12,
              }}
              formatter={(value, name: string) => [
                typeof value === 'number' ? formatValue(value) : 'No visitors',
                variantLabel(name),
              ]}
            />
            <Legend
              wrapperStyle={{ fontSize: 12, color: 'var(--gray-11)' }}
              formatter={(value: string) => variantLabel(value)}
            />
            {variants.map((variant, index) => {
              const color = VARIANT_COLORS[variant.variantName] ?? FALLBACK_COLOR;
              return (
                <Line
                  key={variant.variantId}
                  type="linear"
                  dataKey={variant.variantName}
                  name={variant.variantName}
                  stroke={color}
                  strokeWidth={2}
                  connectNulls={false}
                  dot={{ r: 3, fill: color, strokeWidth: 0 }}
                  activeDot={{ r: 5 }}
                  isAnimationActive
                  animationDuration={800}
                  animationEasing="ease-out"
                  animationBegin={index * 80}
                />
              );
            })}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </Card>
  );
}
